"use client"

import { useEffect, useRef, useState } from "react"
import { Check } from "lucide-react"
import { usePrefersReducedMotion } from "@/components/glass/glass-kit"
import { PaymentMethodLogo, type PaymentMethodKey } from "@/components/landing/payment-method-logos"

const METHODS: PaymentMethodKey[] = ["visa", "mastercard", "apple_pay", "google_pay"]

/**
 * Autopay demo for the owner "Stop chasing rent" card — toggle flips on when
 * scrolled into view, status shifts from due → scheduled. Payment rails below.
 */
export function AutopayArt({ className = "" }: { className?: string }) {
  const reduced = usePrefersReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const [on, setOn] = useState(reduced)
  const [started, setStarted] = useState(reduced)

  useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return

    let timeout = 0

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setStarted(true)
        timeout = window.setTimeout(() => setOn(true), 480)
        io.disconnect()
      },
      { threshold: 0.35 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      if (timeout) window.clearTimeout(timeout)
    }
  }, [reduced])

  const progress = on ? 100 : started ? 62 : 18

  return (
    <div ref={ref} className={`absolute inset-x-0 bottom-0 space-y-2 ${className}`}>
      <div
        className="rounded-[20px] p-3.5 sm:p-4"
        style={{
          background: "var(--pg-card)",
          border: "1px solid var(--pg-card-edge)",
          boxShadow: "inset 0 1px 0 var(--pg-gloss)",
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[9px] font-medium tracking-wide" style={{ color: "var(--pg-muted)" }}>
              Monthly booth rent
            </p>
            <p className="mt-0.5 truncate text-[12px] font-semibold tracking-tight" style={{ color: "var(--pg-fg)" }}>
              BOOTH 3 · Marco
            </p>
          </div>
          <div
            className="relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors duration-500 ease-out"
            style={{ background: on ? "rgba(52,199,89,0.95)" : "var(--pg-line)" }}
            aria-hidden
          >
            <div
              className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-500 ease-out"
              style={{ transform: on ? "translateX(18px)" : "translateX(2px)" }}
            />
          </div>
        </div>

        <div className="mt-2.5 flex items-baseline justify-between gap-2">
          <span className="text-[20px] font-semibold leading-none tracking-[-0.03em] tabular-nums">$600.00</span>
          <span className="text-[9px]" style={{ color: "var(--pg-faint)" }}>
            Visa ···· 4242
          </span>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-[9px] sm:text-[10px]">
            <span style={{ color: "var(--pg-muted)" }}>{on ? "Autopay scheduled" : "Next charge"}</span>
            <span
              className="inline-flex items-center gap-1 font-semibold tabular-nums"
              style={{ color: on ? "rgb(90,214,124)" : "var(--pg-fg)", opacity: on ? 1 : 0.85 }}
            >
              {on ? (
                <>
                  <Check className="h-2.5 w-2.5" strokeWidth={2.5} />
                  Sep 1
                </>
              ) : (
                "Due in 2 days"
              )}
            </span>
          </div>
          <div
            className="relative h-1.5 w-full overflow-hidden rounded-full"
            style={{ background: "var(--pg-line)" }}
          >
            <div
              className="h-full rounded-full transition-[width] duration-700 ease-out"
              style={{
                width: `${progress}%`,
                background: on ? "rgb(90,214,124)" : "var(--pg-muted)",
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {METHODS.map((kind) => (
          <div
            key={kind}
            className="grid h-[48px] place-items-center rounded-[12px] px-1"
            style={{
              background: "var(--pg-card)",
              border: "1px solid var(--pg-card-edge)",
              boxShadow: "inset 0 1px 0 var(--pg-gloss)",
            }}
          >
            <PaymentMethodLogo kind={kind} variant="tile" />
          </div>
        ))}
      </div>
    </div>
  )
}
